import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getTaxGroupById, createTaxGroup, updateTaxGroup, getTaxes } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import Select from "react-select";
import { useTranslation } from "../context/I18nContext";

export default function AddEditTaxGroup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("tax_groups");
  const { t: tCommon } = useTranslation("common");

  const [taxOptions, setTaxOptions] = useState([]);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", is_active: true });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getTaxes(token)
      .then((res) => setTaxOptions((res || []).map((tax) => ({
        value: tax.id,
        label: `${tax.name} (${tax.calculation_method === "fixed_amount" ? tax.rate : `${tax.rate}%`})`,
      }))))
      .catch(() => notify.error(t("notify_load_taxes_failed", "Failed to load taxes")));

    if (id) {
      getTaxGroupById(token, id).then((g) => {
        setForm({
          name: g.name || "",
          description: g.description || "",
          is_active: g.is_active !== undefined ? g.is_active : true,
        });
        setSelectedTaxes((g.items || []).map((it) => ({ value: it.tax_id, label: it.tax?.name || `#${it.tax_id}` })));
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load tax group")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    if (!form.name.trim()) {
      setErrors({ name: t("validation_name_required", "Tax group name is required") });
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    // Array order becomes each item's SortOrder server-side — it matters
    // once compound taxes are actually applied at checkout (Phase 2).
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      is_active: form.is_active,
      tax_ids: selectedTaxes.map((t) => t.value),
    };

    try {
      if (id) {
        await updateTaxGroup(token, id, payload);
        notify.success(t("notify_updated", "Tax group updated successfully"));
      } else {
        await createTaxGroup(token, payload);
        notify.success(t("notify_created", "Tax group created successfully"));
      }
      navigate("/tax-groups");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      padding: '2px',
      borderRadius: '12px',
      borderColor: '#f3f4f6',
      backgroundColor: '#f9fafb80',
      '&:hover': { borderColor: 'var(--primary-color)' },
      boxShadow: 'none',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: 'var(--primary-color)',
      color: 'white',
      borderRadius: '8px',
    }),
    multiValueLabel: (base) => ({ ...base, color: 'white' }),
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/tax-groups")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Tax Group") : t("add_title", "Add New Tax Group")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "This is the name a product actually selects")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <FloatingInput label={t("field_name_label", "Tax Group Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.TaxGroups} />
          <FloatingInput label={t("field_description_label", "Description (optional)")} value={form.description} onChange={setField("description")} />

          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ms-4">
              {t("taxes_included_label", "Taxes Included")}
            </label>
            <Select
              options={taxOptions}
              value={selectedTaxes}
              onChange={setSelectedTaxes}
              isMulti
              placeholder={t("taxes_included_placeholder", "Select one or more taxes...")}
              styles={customSelectStyles}
              className="text-sm"
            />
            <p className="text-[10px] text-gray-400 ms-4 italic">
              {t("taxes_included_hint", "* Only the taxes whose target region matches the branch's location apply automatically at checkout.")}
            </p>
          </div>

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_tax_group", "Update Tax Group") : t("btn_create_tax_group", "Create Tax Group")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/tax-groups")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
